import React, { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import { Dialog } from "primereact/dialog";
import PhotoEvidenceUpload from "../../components/PhotoEvidenceUpload";
import { inspectionRequest, errorMessage } from "./inspectionApi";
import {
  categories,
  RATINGS,
  STATUSES,
  ACTIONS,
  fullName,
  formatDate,
} from "./inspectionUi";

function Photos({ photos, onSelect }) {
  return photos?.length ? (
    <div className="mi-gallery">
      {photos.map((src, index) => (
        <button
          type="button"
          key={index}
          onClick={() => onSelect(src)}
          aria-label={`Ampliar fotografía ${index + 1}`}
        >
          <img src={src} alt={`Evidencia ${index + 1}`} loading="lazy" />
          <span>
            <i className="pi pi-search-plus" />
          </span>
        </button>
      ))}
    </div>
  ) : null;
}
export default function InspectionDetail({
  item,
  onUpdated,
  onHistory,
  onRefresh,
  onDeleted,
}) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [action, setAction] = useState("");
  const [comment, setComment] = useState("");
  const [photos, setPhotos] = useState([]);
  const [assignedTo, setAssignedTo] = useState(null);
  const [operators, setOperators] = useState([]);
  const [image, setImage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const finalized = ["closed", "no_action"].includes(item.status);
  useEffect(() => {
    setAction("");
    setComment("");
    setPhotos([]);
    setAssignedTo(null);
    setError("");
  }, [item._id, item.version]);
  useEffect(() => {
    if (action !== "assign") return;
    let active = true;
    inspectionRequest("/options")
      .then((result) => {
        if (active) setOperators(result.operators);
      })
      .catch((err) => {
        if (active) setError(errorMessage(err));
      });
    return () => {
      active = false;
    };
  }, [action]);
  const perform = async (selectedAction = action) => {
    if (
      ["submit", "return", "support", "comment", "assign"].includes(
        selectedAction
      ) &&
      !comment.trim()
    ) {
      setError("Escribe un comentario para continuar.");
      return;
    }
    if (selectedAction === "submit" && !photos.length) {
      setError("Adjunta al menos una fotografía de la corrección.");
      return;
    }
    if (selectedAction === "assign" && !assignedTo) {
      setError("Selecciona un operador.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const result = await inspectionRequest(`/${item._id}/actions`, {
        action: selectedAction,
        version: item.version,
        comment: ["acknowledge", "start"].includes(selectedAction)
          ? ""
          : comment,
        photos: ["acknowledge", "start"].includes(selectedAction) ? [] : photos,
        ...(selectedAction === "assign" ? { assignedTo } : {}),
      });
      onUpdated(result);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };
  const remove = async () => {
    setSaving(true);
    setDeleteError("");
    try {
      await inspectionRequest(
        `/${item._id}`,
        { version: item.version },
        "DELETE"
      );
      setConfirmDelete(false);
      onDeleted();
    } catch (err) {
      setDeleteError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };
  const choose = (value) => {
    setAction(value);
    setError("");
    setComment("");
    setPhotos([]);
  };
  return (
    <div className="mi-detail">
      <div className={`mi-detail-banner ${item.rating}`}>
        <div>
          <span className={`mi-badge ${item.rating}`}>
            <i className={RATINGS[item.rating].icon} />{" "}
            {RATINGS[item.rating].label}
          </span>
          <h2>{item.snapshot.name}</h2>
          <p>
            {item.snapshot.serial} · {item.snapshot.location || "Sin ubicación"}
          </p>
        </div>
        <span className={`mi-status ${item.status}`}>
          {STATUSES[item.status]}
        </span>
      </div>
      <div className="mi-machine-context">
        <div>
          <small>RUTA</small>
          <strong>{item.snapshot.route || "Sin ruta"}</strong>
        </div>
        <div>
          <small>RESPONSABLE ACTUAL</small>
          <strong>{fullName(item.assignedTo)}</strong>
        </div>
        <div>
          <small>INSPECCIONADA POR</small>
          <strong>{fullName(item.createdBy)}</strong>
          <small>{formatDate(item.createdAt)}</small>
        </div>
      </div>
      {item.permissions.remove && (
        <div
          className="mi-actions"
          style={{ justifyContent: "flex-end", marginBottom: "12px" }}
        >
          <Button
            label="Eliminar inspección"
            icon="pi pi-trash"
            className="p-button-danger p-button-outlined p-button-sm"
            disabled={saving}
            onClick={() => {
              setDeleteError("");
              setConfirmDelete(true);
            }}
          />
        </div>
      )}
      <Dialog
        header="Eliminar inspección"
        visible={confirmDelete}
        onHide={() => {
          if (!saving) setConfirmDelete(false);
        }}
        closable={!saving}
        closeOnEscape={!saving}
        modal
        style={{ width: "min(460px, 94vw)" }}
        footer={
          <div className="mi-actions" style={{ justifyContent: "flex-end" }}>
            <Button
              label="Cancelar"
              className="p-button-text"
              disabled={saving}
              onClick={() => setConfirmDelete(false)}
              autoFocus
            />
            <Button
              label="Sí, eliminar"
              icon="pi pi-trash"
              className="p-button-danger"
              loading={saving}
              onClick={remove}
            />
          </div>
        }
      >
        <p>
          ¿Eliminar la inspección de <strong>{item.snapshot.name}</strong>?
        </p>
        <p>
          Dejará de aparecer en los listados, indicadores e historial de la
          máquina. Sus alertas también se retirarán.
        </p>
        <p className="mi-muted">
          Se conserva un registro interno de quién la eliminó y cuándo.
        </p>
        {deleteError && (
          <div className="mi-error" role="alert">
            {deleteError}
          </div>
        )}
      </Dialog>
      <div className="mi-detail-links">
        <Button
          label="Historial de esta máquina"
          icon="pi pi-history"
          className="p-button-text p-button-sm"
          onClick={() => onHistory(item.machine)}
        />
        <Button
          label="Actualizar"
          icon="pi pi-refresh"
          className="p-button-text p-button-sm"
          onClick={onRefresh}
          disabled={saving}
        />
      </div>
      {item.notificationPending && (
        <div className="mi-notice">
          La inspección está guardada. El envío de la alerta se reintentará
          automáticamente.
        </div>
      )}
      {!item.assignedTo && (
        <div className="mi-notice">
          <i className="pi pi-user-plus" /> Sin responsable. Supervisión puede
          asignar un operador desde las acciones.
        </div>
      )}
      {item.supportRequested && (
        <div className="mi-notice">
          <i className="pi pi-wrench" /> Se solicitó apoyo técnico. Supervisión
          ha sido notificada.
        </div>
      )}
      <section className="mi-detail-section">
        <h3>
          Evaluación de la máquina{" "}
          <small>{item.machineType === "coffee" ? "Café" : "Snack"}</small>
        </h3>
        <div className="mi-evaluation-grid">
          {categories(item.machineType).map((category) => {
            const row = item.checklist.find(
              (entry) => entry.key === category.key
            );
            return (
              <div key={category.key}>
                <span>{category.label}</span>
                <span className={`mi-badge ${row?.rating}`}>
                  {RATINGS[row?.rating]?.label}
                </span>
                {row?.note && <p>{row.note}</p>}
              </div>
            );
          })}
        </div>
        <blockquote>{item.comment}</blockquote>
        <Photos photos={item.photos} onSelect={setImage} />
      </section>
      {!finalized && item.permissions.act && (
        <section className="mi-detail-section mi-action-section">
          <h3>Dar seguimiento</h3>
          <p className="mi-muted">
            La lectura del aviso no cierra la inspección. Cada acción queda en
            el historial.
          </p>
          {error && (
            <div className="mi-error" role="alert">
              {error}
            </div>
          )}
          <div className="mi-actions">
            {item.permissions.acknowledge && !item.acknowledgedAt && (
              <Button
                label="Confirmar recepción"
                icon="pi pi-check"
                className="p-button-outlined"
                disabled={saving}
                onClick={() => perform("acknowledge")}
              />
            )}
            {item.status === "pending" && item.assignedTo && (
              <Button
                label="Iniciar atención"
                icon="pi pi-play"
                disabled={saving}
                onClick={() => perform("start")}
              />
            )}
            {["pending", "in_progress"].includes(item.status) &&
              item.assignedTo && (
                <Button
                  label="Enviar corrección"
                  icon="pi pi-camera"
                  disabled={saving}
                  onClick={() => choose("submit")}
                />
              )}
            {item.status === "verification" && item.permissions.review && (
              <>
                <Button
                  label="Verificar y cerrar"
                  icon="pi pi-check-circle"
                  className="p-button-success"
                  disabled={saving}
                  onClick={() => choose("close")}
                />
                <Button
                  label="Solicitar otra corrección"
                  icon="pi pi-replay"
                  className="p-button-outlined"
                  disabled={saving}
                  onClick={() => choose("return")}
                />
              </>
            )}
            <Button
              label="Comentar"
              icon="pi pi-comment"
              className="p-button-text"
              disabled={saving}
              onClick={() => choose("comment")}
            />
            {!item.supportRequested && (
              <Button
                label="Pedir apoyo técnico"
                icon="pi pi-wrench"
                className="p-button-text"
                disabled={saving}
                onClick={() => choose("support")}
              />
            )}
            {item.permissions.manage && (
              <Button
                label={item.assignedTo ? "Reasignar" : "Asignar responsable"}
                icon="pi pi-user-edit"
                className="p-button-text"
                disabled={saving}
                onClick={() => choose("assign")}
              />
            )}
          </div>
          {action && (
            <div className="mi-action-form">
              <h4>
                {
                  {
                    submit: "Evidencia de la corrección",
                    close: "Verificar y cerrar inspección",
                    return: "Indica qué falta por corregir",
                    comment: "Agregar comentario",
                    support: "Describe el apoyo que necesitas",
                    assign: "Asignar un operador responsable",
                  }[action]
                }
              </h4>
              {action === "close" && (
                <p>
                  Confirma que revisaste las fotografías y que los hallazgos
                  fueron atendidos.
                </p>
              )}
              {action === "assign" && (
                <Dropdown
                  aria-label="Nuevo responsable"
                  value={assignedTo}
                  options={operators}
                  optionLabel="name"
                  optionValue="_id"
                  filter
                  onChange={(e) => setAssignedTo(e.value)}
                  placeholder="Seleccionar operador"
                  className="w-full mb-3"
                  disabled={saving}
                />
              )}
              <InputTextarea
                aria-label="Comentario de seguimiento"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={3}
                maxLength={2000}
                className="w-full"
                placeholder={
                  action === "close"
                    ? "Comentario de verificación (opcional)"
                    : "Comentario obligatorio"
                }
                disabled={saving}
              />
              {["submit", "comment", "support"].includes(action) && (
                <div className="mi-evidence">
                  <PhotoEvidenceUpload
                    label={
                      action === "submit"
                        ? "Fotografías de la corrección *"
                        : "Fotografías adicionales"
                    }
                    value={photos}
                    onChange={setPhotos}
                    maxPhotos={6}
                    maxSizeMB={1}
                    maxTotalBytes={3 * 1024 * 1024}
                    disabled={saving}
                    helperText="Hasta 6 fotos, 1 MB por foto y 3 MB por envío."
                  />
                </div>
              )}
              <div className="mi-actions">
                <Button
                  label={
                    action === "close"
                      ? "Confirmar cierre"
                      : "Guardar y notificar"
                  }
                  icon="pi pi-check"
                  loading={saving}
                  onClick={() => perform()}
                />
                <Button
                  label="Cancelar"
                  className="p-button-text"
                  disabled={saving}
                  onClick={() => choose("")}
                />
              </div>
            </div>
          )}
        </section>
      )}
      <section className="mi-detail-section">
        <h3>Historial de seguimiento</h3>
        <p className="mi-muted">
          Responsable al inspeccionar:{" "}
          {item.snapshot.operatorName || "Sin asignar"}
        </p>
        <ol className="mi-timeline">
          {[...item.history].reverse().map((entry, index) => (
            <li key={index}>
              <span className="mi-timeline-dot">
                <i
                  className={
                    entry.action === "created" ? "pi pi-flag" : "pi pi-check"
                  }
                />
              </span>
              <div>
                <strong>{ACTIONS[entry.action] || entry.action}</strong>
                <small>
                  {entry.actorName} · {formatDate(entry.at)}
                </small>
                {entry.comment && <p>{entry.comment}</p>}
                <Photos photos={entry.photos} onSelect={setImage} />
              </div>
            </li>
          ))}
        </ol>
      </section>
      <Dialog
        header="Fotografía de evidencia"
        visible={Boolean(image)}
        onHide={() => setImage(null)}
        modal
        className="mi-photo-dialog"
        style={{ width: "min(960px, 95vw)" }}
      >
        <img
          src={image || ""}
          alt="Evidencia ampliada de la inspección"
          style={{ width: "100%", maxHeight: "75vh", objectFit: "contain" }}
        />
      </Dialog>
    </div>
  );
}
